import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { LegalDraftBanner } from "./LegalDraftBanner";
import { MarkdownArticle } from "./MarkdownArticle";
import { useUserRoles } from "@/hooks/useUserRoles";

type LegalSlug = "privacy" | "terms" | "accessibility" | "disclosures";

interface LegalPageRow {
  slug: LegalSlug;
  title: string;
  body: string;
  version: string;
  effective_date: string;
  status: "draft" | "published" | "archived";
}

interface Props {
  slug: LegalSlug;
  fallbackTitle: string;
  fallbackHeadline: string;
}

// Renders a single legal document. Reads the live row from `legal_pages`.
// Public visitors only see rows where status='published' (enforced by RLS).
// Admins additionally see drafts so they can preview before publishing.
export function LegalPageLayout({ slug, fallbackTitle, fallbackHeadline }: Props) {
  const { isAdmin } = useUserRoles();
  const [page, setPage] = useState<LegalPageRow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Admins fetch any status; public visitors are filtered to published by RLS.
      let q = supabase
        .from("legal_pages")
        .select("slug, title, body, version, effective_date, status")
        .eq("slug", slug);
      if (!isAdmin) q = q.eq("status", "published");
      const { data } = await q.order("updated_at", { ascending: false }).limit(1).maybeSingle();
      if (!cancelled) {
        setPage((data as LegalPageRow | null) ?? null);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, isAdmin]);

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />

      {/* Hero */}
      <section className="bg-cream-50 px-5 pb-12 pt-28 md:px-10 md:pt-32">
        <div className="mx-auto max-w-5xl text-center">
          <Eyebrow align="center">{fallbackTitle}</Eyebrow>
          <h1 className="mt-6 font-serif text-4xl font-bold text-navy-900 md:text-5xl">
            {page?.title ?? fallbackHeadline}
          </h1>
          {page && (
            <p className="mt-4 font-sans text-sm text-charcoal-500">
              Last updated:{" "}
              {new Date(page.effective_date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}{" "}
              · Version {page.version}
            </p>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="px-5 pb-24 md:px-10">
        <div className="mx-auto max-w-6xl">
          {loading ? (
            <p className="py-20 text-center font-sans text-sm text-charcoal-500">
              Loading…
            </p>
          ) : !page ? (
            <div className="mx-auto max-w-2xl py-20 text-center">
              <p className="font-sans text-charcoal-500">
                This document hasn't been published yet.
              </p>
              <Link
                to="/contact"
                className="mt-6 inline-block font-sans text-sm uppercase tracking-[0.18em] text-bronze-600 hover:text-navy-800"
              >
                Contact us →
              </Link>
            </div>
          ) : (
            <>
              {page.status === "draft" && <LegalDraftBanner />}
              <MarkdownArticle body={page.body} />
              <div className="mx-auto mt-16 max-w-[720px] border-t border-bronze-600/40 pt-8 text-center lg:ml-[244px] lg:mr-0">
                <p className="font-sans text-sm text-charcoal-500">
                  Questions about this document?{" "}
                  <Link
                    to="/contact"
                    className="text-bronze-600 hover:text-navy-800"
                  >
                    Contact us →
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default LegalPageLayout;